/**
 * Build-time configuration, from the deployed stacks' outputs.
 *
 * Nothing here is a secret: the API URL and the Cognito pool/client ids
 * are public by design (the pool client has no client secret — SRP from
 * a browser cannot keep one). Same shape as tengrade's web-examiner
 * config.ts. `hostedUiUrl`/`googleSsoEnabled` are Google sign-in's own
 * addition (ported from tengrade's R3/R7 design) — both optional, unset
 * until EtlGenAuth is deployed with a real `google_client_id` context
 * value (auth_stack.py's own module docstring).
 */

export interface AppConfig {
  apiUrl: string;
  userPoolId: string;
  clientId: string;
  region: string;
  /** Empty until a Google-sign-in-enabled redeploy — unlike the three
   *  fields above, SignIn.tsx's SRP flow never reads it. */
  hostedUiUrl: string;
  googleSsoEnabled: boolean;
  /** The address API keys work against (the stack's `KeyApiUrl`), shown on the API keys page.
   *  Optional like the two above: a build without it just does not show the block. */
  keyApiUrl: string;
}

function read(name: string): string {
  return (import.meta.env[name] as string | undefined)?.trim() ?? '';
}

export const config: AppConfig = {
  apiUrl: read('VITE_API_URL').replace(/\/+$/, ''),
  userPoolId: read('VITE_COGNITO_USER_POOL_ID'),
  clientId: read('VITE_COGNITO_CLIENT_ID'),
  region: read('VITE_AWS_REGION') || 'eu-west-1',
  hostedUiUrl: read('VITE_COGNITO_HOSTED_UI_URL').replace(/\/+$/, ''),
  googleSsoEnabled: read('VITE_GOOGLE_SSO_ENABLED') === 'true',
  keyApiUrl: read('VITE_KEY_API_URL').replace(/\/+$/, ''),
};

/** Missing configuration is shown as a setup screen, not a runtime crash. */
export const missingConfig: string[] = [
  ['VITE_API_URL', config.apiUrl],
  ['VITE_COGNITO_USER_POOL_ID', config.userPoolId],
  ['VITE_COGNITO_CLIENT_ID', config.clientId],
]
  .filter(([, value]) => !value)
  .map(([name]) => name as string);
